/**
 * Shared task-attachment shape & helpers.
 *
 * Extracted verbatim from TaskViewMobile.tsx (Stage 1).
 *
 * Shape returned by the live GET/POST /api/v1/tasks/{id}/attachments handler
 * (ListEntityAttachmentsHandler / UploadEntityAttachmentHandler over the
 * polymorphic entity_attachments table). Photos are detected via the
 * content_type MIME prefix, NOT a `file_type` enum.
 */
import { apiBaseUrl, getToken } from '@/lib/api'
import { isNativePlatform } from '@/lib/native'

export interface Attachment {
  id: number
  url: string
  thumbnail_url?: string
  filename: string
  original_name?: string
  content_type?: string
  file_size?: number
}

export function isImageAttachment(a: Attachment): boolean {
  return isImageFile(a.filename, a.content_type)
}

export function attachmentName(a: Attachment): string {
  return a.original_name || a.filename || 'file'
}

// Auth for media loaded WITHOUT a cookie session: /uploads/ (non-avatar)
// requires auth, which the logged-in web browser supplies via the httpOnly
// ml_session cookie automatically. Where that cookie doesn't exist — the
// Capacitor WebView (#2181) or the public Shop Kiosk, which authenticates
// with a kiosk token and never logs in (#3434) — an <img src> / <iframe> /
// download navigation can't send the Authorization header → 401. The auth
// middleware also accepts `?session_token=` (same JWT/kiosk-token validation
// path the Frigate camera widget uses), so append the caller's token to the
// URL. Skips public avatars and URLs that already carry a token. The cookie
// is httpOnly, so JS cannot detect "cookie present" — callers that KNOW they
// have no cookie session (native, kiosk) opt in explicitly.
export function withSessionToken(url: string, token: string | null | undefined): string {
  if (
    !url || !token ||
    !url.includes('/uploads/') ||
    url.includes('/uploads/avatars/') ||
    /[?&]session_token=/.test(url)
  ) return url
  return url + (url.includes('?') ? '&' : '?') + 'session_token=' + encodeURIComponent(token)
}

// §mobile-native-fetch-base-url: the API returns storage-relative paths
// (e.g. "/uploads/chat/5/x.png"). That resolves fine on web (origin is
// app.usetenon.com) but not inside the Capacitor WebView, whose origin is
// localhost — so every attachment src/href must route through apiBaseUrl,
// same as the JSON fetches already do.
function absolutize(url: string | undefined): string {
  if (!url) return ''
  const full = /^https?:\/\//i.test(url) ? url : `${apiBaseUrl}${url}`
  if (isNativePlatform()) return withSessionToken(full, getToken())
  return full
}

// Loosened to a structural shape (not the full `Attachment` interface) so
// callers with a differently-shaped attachment type (e.g. task-detail-peek's
// PeekAttachment, which uses `mime_type` + a string `id`) can still route
// through the ONE shared resolver instead of hand-prefixing per call site —
// that divergence is exactly the #1534 bug class.
export function attachmentUrl(a: { url?: string }): string {
  return absolutize(a.url)
}

export function attachmentThumbUrl(a: { url?: string; thumbnail_url?: string }): string {
  return absolutize(a.thumbnail_url) || attachmentUrl(a)
}

// ─── File-type classification ──────────────────────────────────────────────
//
// Canonical classifiers for the multi-type attachment viewer (#1535). Take
// plain (filename, mimeType) args rather than an object so callers with
// different attachment shapes (content_type vs mime_type field names) can
// all route through the same logic without a type-unification refactor.

export function isImageFile(filename: string | undefined, mimeType: string | undefined): boolean {
  if ((mimeType ?? '').startsWith('image/')) return true
  return /\.(png|jpe?g|gif|webp|heic|heif|bmp|svg)$/i.test(filename ?? '')
}

export function isPdfFile(filename: string | undefined, mimeType: string | undefined): boolean {
  if (mimeType === 'application/pdf') return true
  return /\.pdf$/i.test(filename ?? '')
}

export function isVideoFile(filename: string | undefined, mimeType: string | undefined): boolean {
  if ((mimeType ?? '').startsWith('video/')) return true
  return /\.(mp4|mov|webm|avi|m4v)$/i.test(filename ?? '')
}

export type FileKind = 'image' | 'pdf' | 'video' | 'other'

export function fileKindOf(filename: string | undefined, mimeType: string | undefined): FileKind {
  if (isImageFile(filename, mimeType)) return 'image'
  if (isPdfFile(filename, mimeType)) return 'pdf'
  if (isVideoFile(filename, mimeType)) return 'video'
  return 'other'
}

export function formatFileSize(bytes: number | undefined): string | null {
  if (bytes == null || Number.isNaN(bytes)) return null
  if (bytes < 1024) return `${bytes} B`
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`
}
